import { makeObservable, observable, action } from 'mobx';
import { IAttachment } from '../../../global/types/types';
import { IRoundsRow } from '../../../swagger/gd/round';
import { RequestType } from './RequestTypeDialogStore';

// Typy príloh zodpovedajúce dropedFiles0..5 zo zp RequestFormStore.js
// dropedFiles0 → GD:GDREQ:ENERGETICAUDIT
// dropedFiles1 → GD:GDREQ:OZE
// dropedFiles2 → GD:GDREQ:MINIMALDECLARATION
// dropedFiles3 → GD:GDREQ:DIVISIONACTIVITY
// dropedFiles4 → GD:GDREQ:POWEROFATTORNEY
// dropedFiles5 → GD:GDREQ:AFFIDAVIT
// (dropedFiles6 existoval v zp, ale nebol mapovaný na žiadny attachment type — vynechávame)
export const ATTACHMENT_KEYS = [
  'GD:GDREQ:ENERGETICAUDIT',
  'GD:GDREQ:OZE',
  'GD:GDREQ:MINIMALDECLARATION',
  'GD:GDREQ:DIVISIONACTIVITY',
  'GD:GDREQ:POWEROFATTORNEY',
  'GD:GDREQ:AFFIDAVIT'
] as const;

export type AttachmentKey = (typeof ATTACHMENT_KEYS)[number];

// Prílohy žiadosti — sekcia zobrazená v kroku 2 (Údaje o projekte) len pri reimbursement toku
// ("Zariadenie už mám nainštalované"), doklady preukazujúce už vykonanú inštaláciu.
export const INSTALLED_ATTACHMENT_KEYS = [
  'GD:GDREQ:INSTALLATIONINVOICE',
  'GD:GDREQ:INSTALLATIONPAYMENT',
  'GD:GDREQ:AUDITORCONFIRMATION'
] as const;

export type InstalledAttachmentKey = (typeof INSTALLED_ATTACHMENT_KEYS)[number];

class RequestFormStore {
  readonlyForm: boolean = false;
  // requestInProgress: zobrazí Uložiť/Odoslať (nová žiadosť alebo načítaná 'inp')
  requestInProgress: boolean = false;
  // showEditButton: zobrazí tlačidlo Upraviť/Zahodiť (načítaná 'inp')
  showEditButton: boolean = false;
  roundsData: IRoundsRow[] = [];
  showSwift: boolean = false;
  // Voľba z RequestTypeDialog — 'reimbursement' rozširuje tabuľku miest o stĺpce nainštalovaného zariadenia
  requestType: RequestType = 'reservation';
  attachments: Record<AttachmentKey | InstalledAttachmentKey, IAttachment[]> = {
    'GD:GDREQ:ENERGETICAUDIT': [],
    'GD:GDREQ:OZE': [],
    'GD:GDREQ:MINIMALDECLARATION': [],
    'GD:GDREQ:DIVISIONACTIVITY': [],
    'GD:GDREQ:POWEROFATTORNEY': [],
    'GD:GDREQ:AFFIDAVIT': [],
    'GD:GDREQ:INSTALLATIONINVOICE': [],
    'GD:GDREQ:INSTALLATIONPAYMENT': [],
    'GD:GDREQ:AUDITORCONFIRMATION': []
  };

  constructor() {
    makeObservable(this, {
      readonlyForm: observable,
      requestInProgress: observable,
      showEditButton: observable,
      roundsData: observable,
      showSwift: observable,
      requestType: observable,
      attachments: observable,
      setReadonly: action,
      setRequestInProgress: action,
      setShowEditButton: action,
      setRoundsData: action,
      setShowSwift: action,
      setRequestType: action,
      setAttachments: action,
      reset: action
    });
  }

  setReadonly = (value: boolean) => {
    this.readonlyForm = value;
  };

  setRequestInProgress = (value: boolean) => {
    this.requestInProgress = value;
  };

  setShowEditButton = (value: boolean) => {
    this.showEditButton = value;
  };

  setRoundsData = (rows: IRoundsRow[]) => {
    this.roundsData = rows;
  };

  setShowSwift = (value: boolean) => {
    this.showSwift = value;
  };

  setRequestType = (value: RequestType) => {
    this.requestType = value;
  };

  setAttachments = (key: AttachmentKey | InstalledAttachmentKey, files: IAttachment[]) => {
    this.attachments[key] = files;
  };

  reset = () => {
    this.readonlyForm = false;
    this.requestInProgress = false;
    this.showEditButton = false;
    this.roundsData = [];
    this.showSwift = false;
    this.requestType = 'reservation';
    this.attachments = {
      'GD:GDREQ:ENERGETICAUDIT': [],
      'GD:GDREQ:OZE': [],
      'GD:GDREQ:MINIMALDECLARATION': [],
      'GD:GDREQ:DIVISIONACTIVITY': [],
      'GD:GDREQ:POWEROFATTORNEY': [],
      'GD:GDREQ:AFFIDAVIT': [],
      'GD:GDREQ:INSTALLATIONINVOICE': [],
      'GD:GDREQ:INSTALLATIONPAYMENT': [],
      'GD:GDREQ:AUDITORCONFIRMATION': []
    };
  };
}

const singleton = new RequestFormStore();
export default singleton;
